import React, { useEffect, useState } from "react";
import "../styles/AdaptiveReport.css";
import axiosClient from "../api/axiosClient";
import Chart from "chart.js/auto";

import {
  Trophy,
  AlertTriangle,
  Clock3,
  BarChart3,
  LayoutDashboard,
} from "lucide-react";
import {useNavigate} from 'react-router-dom'

const AdaptiveReport = () => {

  const navigate= useNavigate();
  const [analyticsData, setAnalyticsData] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  // ================= FETCH DATA =================
  useEffect(() => {
    const fetchAnalytics =
      async () => {
        try {
          const token =
            localStorage.getItem(
              "token"
            );

          const res = await axiosClient.get(
            "/auth/adaptiveTestDashboard",
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const data = res.data;

          // BEST TOPIC
          const bestTopic = [
            ...data.topicWiseBreakdown,
          ].sort(
            (a, b) =>
              b.topicAccuracy -
              a.topicAccuracy
          )[0];

          // WEAK TOPIC
          const weakTopic = [
            ...data.topicWiseBreakdown,
          ].sort(
            (a, b) =>
              a.topicAccuracy -
              b.topicAccuracy
          )[0];

          setAnalyticsData({
            totalQuestions:
              data.totalQuestionsAttended,

            totalCorrect:
              data.totalCorrectCount,

            totalWrong:
              data.totalIncorrectCount,

            overallAccuracy:
              data.overallAccuracyRate.toFixed(
                1
              ),

            totalTopics:
              data.topicWiseBreakdown
                .length,

            topics:
              data.topicWiseBreakdown,

            difficulty:
              data.difficultyWisePerformance,

            bestTopic,

            weakTopic,
          });
        } catch (error) {
          console.log(error);

          if (
            error.response?.status ===
            401
          ) {
            localStorage.removeItem(
              "token"
            );

            window.location.href =
              "/login";
          }
        } finally {
          setLoading(false);
        }
      };

    fetchAnalytics();
  }, []);

  // ================= CHARTS =================
  useEffect(() => {
 if (
    !analyticsData ||
    !analyticsData?.topics?.length
  ) {
    return;
  }

  const donutCanvas =
    document.getElementById(
      "analyticsDonutChart"
    );

  const barCanvas =
    document.getElementById(
      "analyticsBarChart"
    );

  if (!donutCanvas || !barCanvas) {
    return;
  }

  Chart.getChart(
    "analyticsDonutChart"
  )?.destroy();

  Chart.getChart(
    "analyticsBarChart"
  )?.destroy();

    // DONUT
    new Chart(
      donutCanvas
      ,
      {
        type: "doughnut",

        data: {
          labels: [
            "Correct",
            "Wrong",
          ],

          datasets: [
            {
              data: [
                analyticsData.totalCorrect,
                analyticsData.totalWrong,
              ],

              backgroundColor: [
                "#22c55e",
                "#ef4444",
              ],

              borderWidth: 0,
            },
          ],
        },

        options: {
          cutout: "72%",

          plugins: {
            legend: {
              display: false,
            },
          },
        },
      }
    );

    // BAR CHART
    new Chart(
     barCanvas
      ,
      {
        type: "bar",

        data: {
          labels:
            analyticsData.topics.map(
              (item) =>
                item.topicName
            ),

          datasets: [
            {
              label: "Correct",

              data:
                analyticsData.topics.map(
                  (item) =>
                    item.correctAnswers
                ),

              backgroundColor:
                "#22c55e",
            },

            {
              label: "Wrong",

              data:
                analyticsData.topics.map(
                  (item) =>
                    item.questionsAttempted -
                    item.correctAnswers
                ),

              backgroundColor:
                "#ef4444",
            },
          ],
        },

        options: {
          responsive: true,

          maintainAspectRatio: false,
        },
      }
    );
  }, [analyticsData]);

if (loading) {
  return (
    <div className="analytics-page-state">
      <div className="analytics-state-card">
        <div className="analytics-loader"></div>

        <h2>
          Loading Analytics...
        </h2>

        <p>
          Please wait while we prepare
          your assessment report.
        </p>
      </div>
    </div>
  );
}

// ================= NO REPORT =================

if (
  !analyticsData ||

  analyticsData.totalQuestions === 0 ||

 analyticsData.topics.length === 0
) {
  return (
    <div className="analytics-page-state">
      <div className="analytics-state-card analytics-empty-card">

        <div className="analytics-empty-icon">
          📊
        </div>

        <h2>
          No Analytics Available
        </h2>

        <p>
          You haven't attempted any
          assessments yet.
        </p>

        <div className="analytics-empty-actions">
          <button onClick={()=>navigate("/adaptiveform")} className="analytics-start-btn">
            Start Assessment
          </button>

          <button onClick={()=>navigate("/front")} className="analytics-dashboard-btn">
            <LayoutDashboard size={16} />
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="analytics-page">
      <div className="analytics-container">

        {/* HEADER */}
        <div className="analytics-header-card">
          <div>
            <h1>
              Assessment Analytics
            </h1>

            <p>
              {
                analyticsData.totalQuestions
              }{" "}
              Questions •{" "}
              {
                analyticsData.totalTopics
              }{" "}
              Topics
            </p>
          </div>

          <div className="analytics-header-actions">
            <button
              onClick={() => navigate("/front")}
              className="analytics-dashboard-btn"
            >
              <LayoutDashboard size={16} />
              Go to Dashboard
            </button>

            <div className="analytics-status-badge">
              <BarChart3 size={16} />
              Performance Report
            </div>
          </div>
        </div>

        {/* STATS */}
        <div className="analytics-stats-grid">

          <div className="analytics-stat-card">
            <span>
              Questions
            </span>

            <h2>
              {
                analyticsData.totalQuestions
              }
            </h2>

            <p>Attempted</p>
          </div>

          <div className="analytics-stat-card">
            <span>Correct</span>

            <h2 className="analytics-green">
              {
                analyticsData.totalCorrect
              }
            </h2>

            <p>
              Correct Answers
            </p>
          </div>

          <div className="analytics-stat-card">
            <span>Wrong</span>

            <h2 className="analytics-red">
              {
                analyticsData.totalWrong
              }
            </h2>

            <p>
              Incorrect Answers
            </p>
          </div>

          <div className="analytics-stat-card">
            <span>
              Accuracy
            </span>

            <h2 className="analytics-yellow">
              {
                analyticsData.overallAccuracy
              }
              %
            </h2>

            <p>
              Overall Accuracy
            </p>
          </div>
        </div>

        {/* CHART SECTION */}
        <div className="analytics-chart-grid">

          {/* DONUT */}
          <div className="analytics-card">
            <div className="analytics-section-title">
              Score Breakdown
            </div>

            <div className="analytics-donut-wrapper">
              <canvas id="analyticsDonutChart"></canvas>

              <div className="analytics-donut-center">
                <h2>
                  {
                    analyticsData.overallAccuracy
                  }
                  %
                </h2>

                <span>
                  Accuracy
                </span>
              </div>
            </div>
          </div>

          {/* TOPIC ACCURACY */}
          <div className="analytics-card">
            <div className="analytics-section-title">
              Topic Accuracy
            </div>

            <div className="analytics-topic-list">
              {analyticsData.topics.map(
                (
                  topic,
                  index
                ) => (
                  <div
                    key={index}
                    className="analytics-topic-item"
                  >
                    <div className="analytics-topic-row">
                      <span>
                        {
                          topic.topicName
                        }
                      </span>

                      <span>
                        {
                          topic.topicAccuracy
                        }
                        %
                      </span>
                    </div>

                    <div className="analytics-progress-bar">
                      <div
                        className="analytics-progress-fill"
                        style={{
                          width: `${topic.topicAccuracy}%`,
                        }}
                      />
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* BAR CHART */}
        <div className="analytics-card analytics-chart-card">
          <div className="analytics-section-title">
            Topic Performance
          </div>

          <div className="analytics-bar-wrapper">
            <canvas id="analyticsBarChart"></canvas>
          </div>
        </div>

        {/* INSIGHTS */}
        <div className="analytics-insight-grid">

          <div className="analytics-insight-card">
            <Trophy className="analytics-green-icon" />

            <div>
              <h3>
                Strongest Topic
              </h3>

              <p>
                {
                  analyticsData
                    .bestTopic
                    .topicName
                }
              </p>

              <span>
                {
                  analyticsData
                    .bestTopic
                    .topicAccuracy
                }
                % Accuracy
              </span>
            </div>
          </div>

          <div className="analytics-insight-card">
            <AlertTriangle className="analytics-red-icon" />

            <div>
              <h3>
                Needs Focus
              </h3>

              <p>
                {
                  analyticsData
                    .weakTopic
                    .topicName
                }
              </p>

              <span>
                {
                  analyticsData
                    .weakTopic
                    .topicAccuracy
                }
                % Accuracy
              </span>
            </div>
          </div>

          <div className="analytics-insight-card">
            <Clock3 className="analytics-purple-icon" />

            <div>
              <h3>
                Total Topics
              </h3>

              <p>
                {
                  analyticsData.totalTopics
                }
              </p>

              <span>
                Covered Topics
              </span>
            </div>
          </div>
        </div>

        {/* DIFFICULTY SECTION */}
       {/* ================= DIFFICULTY SECTION ================= */}

<div className="analytics-levels-wrapper">

  {/* EASY */}
  <div className="analytics-level-card analytics-easy-card">

    <div className="analytics-level-top">
      <div>
        <p className="analytics-level-label">
          EASY LEVEL
        </p>

        <h2>
          {
            analyticsData?.difficulty?.EASY?.length || 0
          }{" "}
          Topics
        </h2>
      </div>

      <div className="analytics-level-badge easy-badge">
        EASY
      </div>
    </div>

    <div className="analytics-level-list">
      {analyticsData?.difficulty?.EASY?.map(
        (item, index) => {
          const percentage =
            (
              item.correctAnswers /
              item.totalQuestions
            ) * 100;

          return (
            <div
              key={index}
              className="analytics-level-item"
            >
              <div className="analytics-level-item-top">
                <h4>
                  {item.topicName}
                </h4>

                <span>
                  {percentage.toFixed(0)}%
                </span>
              </div>

              <div className="analytics-level-progress">
                <div
                  className="analytics-level-progress-fill easy-fill"
                  style={{
                    width: `${percentage}%`,
                  }}
                />
              </div>

              <div className="analytics-level-bottom">
                <span>
                  {
                    item.correctAnswers
                  }
                  /
                  {
                    item.totalQuestions
                  }{" "}
                  Correct
                </span>

                <span>
                  ⏱{" "}
                  {
                    item.avgTimeTaken
                  }
                  s
                </span>
              </div>
            </div>
          );
        }
      )}
    </div>
  </div>

  {/* MEDIUM */}
  <div className="analytics-level-card analytics-medium-card">

    <div className="analytics-level-top">
      <div>
        <p className="analytics-level-label">
          MEDIUM LEVEL
        </p>

        <h2>
          {
            analyticsData?.difficulty?.MEDIUM?.length || 0
          }{" "}
          Topics
        </h2>
      </div>

      <div className="analytics-level-badge medium-badge">
        MEDIUM
      </div>
    </div>

    <div className="analytics-level-list">
      {analyticsData?.difficulty?.MEDIUM?.map(
        (item, index) => {
          const percentage =
            (
              item.correctAnswers /
              item.totalQuestions
            ) * 100;

          return (
            <div
              key={index}
              className="analytics-level-item"
            >
              <div className="analytics-level-item-top">
                <h4>
                  {item.topicName}
                </h4>

                <span>
                  {percentage.toFixed(0)}%
                </span>
              </div>

              <div className="analytics-level-progress">
                <div
                  className="analytics-level-progress-fill medium-fill"
                  style={{
                    width: `${percentage}%`,
                  }}
                />
              </div>

              <div className="analytics-level-bottom">
                <span>
                  {
                    item.correctAnswers
                  }
                  /
                  {
                    item.totalQuestions
                  }{" "}
                  Correct
                </span>

                <span>
                  ⏱{" "}
                  {
                    item.avgTimeTaken
                  }
                  s
                </span>
              </div>
            </div>
          );
        }
      )}
    </div>
  </div>

  {/* HARD */}
  <div className="analytics-level-card analytics-hard-card">

    <div className="analytics-level-top">
      <div>
        <p className="analytics-level-label">
          HARD LEVEL
        </p>

        <h2>
          {
            analyticsData?.difficulty?.HARD?.length || 0
          }{" "}
          Topics
        </h2>
      </div>

      <div className="analytics-level-badge hard-badge">
        HARD
      </div>
    </div>

    <div className="analytics-level-list">
      {analyticsData?.difficulty?.HARD?.map(
        (item, index) => {
          const percentage =
            (
              item.correctAnswers /
              item.totalQuestions
            ) * 100;

          return (
            <div
              key={index}
              className="analytics-level-item"
            >
              <div className="analytics-level-item-top">
                <h4>
                  {item.topicName}
                </h4>

                <span>
                  {percentage.toFixed(0)}%
                </span>
              </div>

              <div className="analytics-level-progress">
                <div
                  className="analytics-level-progress-fill hard-fill"
                  style={{
                    width: `${percentage}%`,
                  }}
                />
              </div>

              <div className="analytics-level-bottom">
                <span>
                  {
                    item.correctAnswers
                  }
                  /
                  {
                    item.totalQuestions
                  }{" "}
                  Correct
                </span>

                <span>
                  ⏱{" "}
                  {
                    item.avgTimeTaken
                  }
                  s
                </span>
              </div>
            </div>
          );
        }
      )}
    </div>
  </div>
</div>
      </div>
    </div>
  );
};

export default AdaptiveReport;